import { useMutation, useQuery, useQueryClient } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";

// Types
export interface Comment {
    id: number;
    content: string;
    originalContent?: string;
    originalLocale?: string | null;
    displayedLocale?: string | null;
    translationStatus?: "original" | "translated" | "missing" | "pending";
    hasTranslation?: boolean;
    availableTranslations?: string[];
    author: {
        id: number;
        firstname: string;
        lastname: string;
        avatarUrl?: string;
        organizationNames?: string[];
        canView: boolean;
        canModify: boolean;
        canDelete: boolean;
    };
    parentComment?: Comment;
    replies?: Comment[];
    createdAt: string;
    updatedAt: string;
    canView: boolean;
    canModify: boolean;
    canDelete: boolean;
    // Reaction data
    reactionsSummary?: Record<string, number>;
    reactionCount?: number;
    userReaction?: string;
}

export interface CreateCommentData {
    content: string;
    post: string; // Post IRI (e.g., "/api/social/posts/1")
    parentComment?: string; // Parent comment IRI for replies
}

export interface UpdateCommentData {
    content: string;
}

// Query keys
const COMMENTS_KEY = "comments";
export const POST_COMMENTS_KEY = "post-comments";

// Get comments for a specific post
export const useGetPostComments = (postId: number, enabled = true) => {
    return useQuery({
        queryKey: [POST_COMMENTS_KEY, postId],
        queryFn: async () => {
            const response = await loggedInApiCall.get("/api/post_comments", {
                params: {
                    post: `/api/social/posts/${postId}`,
                },
            });

            // Transform API Platform Hydra collection to expected format
            const hydraData = response.data;
            const comments = hydraData["hydra:member"] || [];

            return comments as Comment[];
        },
        enabled: !!postId && enabled,
        staleTime: 1000 * 60 * 1, // 1 minute
    });
};

// Create a new comment
export const useCreateComment = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ postId, data }: { postId: number; data: Omit<CreateCommentData, "post"> }) => {
            const commentData: CreateCommentData = {
                ...data,
                post: `/api/social/posts/${postId}`,
            };
            const response = await loggedInApiCall.post("/api/post_comments", commentData);
            return response.data as Comment;
        },
        onSuccess: (newComment, { postId }) => {
            // Invalidate post comments
            queryClient.invalidateQueries([POST_COMMENTS_KEY, postId]);

            // Invalidate trending topics cache as comments affect trending activity
            queryClient.invalidateQueries(["trendingTopics"]);

            const commentsQueryKey = [POST_COMMENTS_KEY, postId];
            const existingComments = queryClient.getQueryData<Comment[]>(commentsQueryKey);
            if (!existingComments) return;

            // If it's a reply, find the parent and add it to replies.
            if (newComment.parentComment) {
                queryClient.setQueryData<Comment[]>(
                    commentsQueryKey,
                    existingComments.map((comment) => {
                        if (comment.id === newComment.parentComment?.id) {
                            return {
                                ...comment,
                                replies: [...(comment.replies || []), newComment],
                            };
                        }
                        return comment;
                    }),
                );
                return;
            }

            // If it's a top-level comment, add it to the beginning.
            queryClient.setQueryData<Comment[]>(commentsQueryKey, [newComment, ...existingComments]);
        },
    });
};

// Update a comment
export const useUpdateComment = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ commentId, data }: { commentId: number; data: UpdateCommentData }) => {
            const response = await loggedInApiCall.put(`/api/post_comments/${commentId}`, data);
            return response.data as Comment;
        },
        onSuccess: (updatedComment) => {
            // Invalidate all related queries
            queryClient.invalidateQueries([POST_COMMENTS_KEY]);
            queryClient.invalidateQueries([COMMENTS_KEY, updatedComment.id]);
        },
    });
};

// Delete a comment
export const useDeleteComment = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (commentId: number) => {
            await loggedInApiCall.delete(`/api/post_comments/${commentId}`);
            return commentId;
        },
        onSuccess: (deletedCommentId) => {
            // Invalidate all related queries
            queryClient.invalidateQueries([POST_COMMENTS_KEY]);

            // Remove comment from all caches
            queryClient.setQueriesData([POST_COMMENTS_KEY], (oldData: any) => {
                if (!oldData) return oldData;

                const removeComment = (comments: Comment[]): Comment[] => {
                    return comments
                        .filter((comment) => comment.id !== deletedCommentId)
                        .map((comment) => ({
                            ...comment,
                            replies: comment.replies ? removeComment(comment.replies) : [],
                        }));
                };

                return removeComment(oldData);
            });
        },
    });
};

// Get single comment (for replies, etc.)
export const useGetComment = (commentId: number) => {
    return useQuery({
        queryKey: [COMMENTS_KEY, commentId],
        queryFn: async () => {
            const response = await loggedInApiCall.get(`/api/post_comments/${commentId}`);
            return response.data as Comment;
        },
        enabled: !!commentId,
        staleTime: 1000 * 60 * 5, // 5 minutes
    });
};
